// src/hooks/useLazyThumbnail.js
// React hook for lazy loading slide thumbnails

import { useState, useEffect, useCallback } from 'react';
import { generateSlideThumbnail, uploadThumbnailToSupabase } from '../utils/thumbnailGenerator';
import { ThumbnailCache } from '../utils/thumbnailCache';

/**
 * Hook to lazy load slide thumbnails
 * @param {Object} slide - Slide data
 * @param {string} projectId - Project ID
 * @param {boolean} enabled - Whether to load thumbnail (default: true)
 * @returns {Object} { thumbnail, loading, error, regenerate }
 */
export function useLazyThumbnail(slide, projectId, enabled = true) {
  const [thumbnail, setThumbnail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateThumbnail = useCallback(async () => {
    if (!slide || !projectId || !enabled) {
      return;
    }

    const slideNumber = slide.slide_number || 1;

    setLoading(true);
    setError(null);

    try {
      // 1. Check if slide already has thumbnail_url from backend
      if (slide.thumbnail_url && !slide.thumbnail_url.includes('/thumbnail/')) {
        console.log(`✅ Using existing thumbnail for slide ${slideNumber}`);
        setThumbnail(slide.thumbnail_url);
        setLoading(false);
        return;
      }

      // 2. Check cache
      const cached = await ThumbnailCache.get(projectId, slideNumber);
      if (cached) {
        setThumbnail(cached);
        setLoading(false);
        return;
      }

      // 3. Generate new thumbnail
      console.log(`📸 Generating thumbnail for slide ${slideNumber}...`);
      const thumbnailBase64 = await generateSlideThumbnail(slide);

      // 4. Try to upload to Supabase
      let thumbnailUrl = thumbnailBase64; // Default to base64
      try {
        const uploadedUrl = await uploadThumbnailToSupabase(thumbnailBase64, projectId, slideNumber);
        if (uploadedUrl) {
          thumbnailUrl = uploadedUrl;
          console.log(`☁️ Uploaded thumbnail for slide ${slideNumber}: ${uploadedUrl}`);
        }
      } catch (uploadError) {
        console.warn(`⚠️ Failed to upload thumbnail for slide ${slideNumber}, using base64:`, uploadError);
      }

      // 5. Cache it
      ThumbnailCache.set(projectId, slideNumber, thumbnailUrl);

      // 6. Set state
      setThumbnail(thumbnailUrl);
      setError(null);
    } catch (err) {
      console.error(`❌ Error generating thumbnail for slide ${slideNumber}:`, err);
      setError(err.message || 'Failed to generate thumbnail');
      setThumbnail(null);
    } finally {
      setLoading(false);
    }
  }, [slide, projectId, enabled]);

  // Generate thumbnail on mount or when slide changes
  useEffect(() => {
    generateThumbnail();
  }, [generateThumbnail]);

  return {
    thumbnail,
    loading,
    error,
    regenerate: generateThumbnail // Allow manual regeneration
  };
}

/**
 * Hook to preload thumbnails for multiple slides
 * @param {Array} slides - Array of slides
 * @param {string} projectId - Project ID
 * @param {number} startIndex - Index to start preloading from (default: 1, skip first)
 * @param {number} batchSize - How many to preload at once (default: 3)
 */
export function usePreloadThumbnails(slides, projectId, startIndex = 1, batchSize = 3) {
  const [preloadStatus, setPreloadStatus] = useState({});

  useEffect(() => {
    if (!slides || !projectId || slides.length <= startIndex) {
      return;
    }

    let cancelled = false;

    const preloadBatch = async () => {
      const slidesToPreload = slides.slice(startIndex, startIndex + batchSize);

      for (const slide of slidesToPreload) {
        if (cancelled) break;

        const slideNumber = slide.slide_number || 1;

        // Skip if already preloaded
        if (preloadStatus[slideNumber]) continue;

        try {
          // Check cache first
          const cached = await ThumbnailCache.get(projectId, slideNumber);
          if (cached) {
            setPreloadStatus(prev => ({ ...prev, [slideNumber]: 'cached' }));
            continue;
          }

          // Generate thumbnail
          setPreloadStatus(prev => ({ ...prev, [slideNumber]: 'loading' }));
          const thumbnailBase64 = await generateSlideThumbnail(slide);

          // Upload to Supabase in background
          uploadThumbnailToSupabase(thumbnailBase64, projectId, slideNumber)
            .then(url => {
              if (url) {
                ThumbnailCache.set(projectId, slideNumber, url);
              } else {
                ThumbnailCache.set(projectId, slideNumber, thumbnailBase64);
              }
            })
            .catch(err => console.warn(`Preload upload failed for slide ${slideNumber}:`, err));

          setPreloadStatus(prev => ({ ...prev, [slideNumber]: 'completed' }));
        } catch (error) {
          console.error(`Preload failed for slide ${slideNumber}:`, error);
          setPreloadStatus(prev => ({ ...prev, [slideNumber]: 'error' }));
        }
      }
    };

    // Start preloading after a short delay
    const timer = setTimeout(() => preloadBatch(), 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [slides, projectId, startIndex, batchSize, preloadStatus]);

  return preloadStatus;
}

export default useLazyThumbnail;
